import '@/assets/style/globalModal.css';
import { Modal } from 'antd';
import deletes from '@/assets/images/delete.png';
import multiply from '@/assets/images/multiply.png';
import cancel from '@/assets/images/cancel.png';
import tick from '@/assets/images/tick.png';
import update from '@/assets/images/updated.png';
import alert from '@/assets/images/warning.png';
import PropTypes from 'prop-types';
import { AntButton } from '@/common/ant-components/AntButton';

/**
 * GlobalModal component to show various types of modals based on the action type.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.isVisible - Determines if the modal is visible.
 * @param {Function} props.onClose - Function to call when the modal is closed.
 * @param {Function} props.onConfirm - Function to call when the confirm button is clicked.
 * @param {string} props.type - Type of modal to display ('delete', 'update', 'create', 'cancel', 'logout', 'previousPath').
 * @param {string} props.customContent - Custom Message to be displayed.
 * @param {boolean} props.centered - Custom Message to be displayed.
 * @returns {JSX.Element} - Rendered GlobalModal component.
 */
const GlobalModal = ({
  isVisible,
  onClose,
  onConfirm,
  type,
  remarks,
  loading,
  customContent,
  centered = true,
}) => {
  let title = '';
  let content = '';
  let buttonText = '';
  let ModalIcon = null;

  // Determine modal content based on the type
  switch (type) {
    case 'delete':
      title = 'Confirm Deletion';
      content = 'Are you sure you want to delete this item?';
      buttonText = 'Delete';
      ModalIcon = deletes;
      break;

    case 'update':
      title = 'Confirm Update';
      content = 'Are you sure you want to update this item?';
      buttonText = 'Update';
      ModalIcon = update;
      break;

    case 'create':
      title = 'Confirm Creation';
      content = 'Are you sure you want to create this item?';
      buttonText = 'Create';
      ModalIcon = tick;
      break;

    case 'cancel':
      title = 'Confirm Cancel';
      content = 'Are you sure you want to cancel this action?';
      buttonText = 'OK';
      ModalIcon = multiply;
      break;

    case 'cancelItem':
      title = 'Confirm Cancel';
      content = 'Are you sure you want to cancel this bill?';
      buttonText = 'OK';
      ModalIcon = cancel;
      break;

    case 'logout':
      title = 'Confirm Logout';
      content = 'Are you sure you want to logout?';
      buttonText = 'Logout';
      ModalIcon = alert;
      break;

    case 'previousPath':
      title = 'Confirm Changes';
      content = 'Are you sure you want to leave , All your data will be erased';
      buttonText = 'Ok';
      ModalIcon = alert;
      break;

    default:
      break;
  }

  return (
    <Modal
      className="custom-modal"
      title={<span className="modal-title">{title}</span>}
      open={isVisible}
      onCancel={onClose}
      onOk={onConfirm}
      closeIcon={false}
      width={400}
      centered={centered}
      footer={[
        <AntButton key="submit" onClick={onConfirm} loading={loading}>
          {type === 'cancelItem' ? 'Yes' : buttonText}
        </AntButton>,
        <AntButton
          key="back"
          onClick={onClose}
          style={{ marginRight: 8 }}
          color={`${type === 'cancelItem' ? 'yellow' : 'red'}`}
        >
          {type === 'cancelItem' ? 'No' : 'Cancel'}
        </AntButton>,
      ]}
    >
      <div className="modal-content">
        {ModalIcon && (
          <div className="gif-container">
            <img src={ModalIcon} alt="Modal GIF" className="modal-gif" />
          </div>
        )}

        <p className="modal-text">{customContent || content}</p>
        {type === 'cancelItem' && remarks}
      </div>
    </Modal>
  );
};

GlobalModal.propTypes = {
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  isVisible: PropTypes.bool,
  type: PropTypes.string,
};

export default GlobalModal;
